import { useState, useEffect } from 'react';
import { getItems, updateItem, FIG_LISTS } from '../../api/figApi';
import generateACORD130 from './generateACORD130';
import './FIG.css';

// Map SharePoint EntityType → internal checkbox key
const ENTITY_TYPE_MAP = {
  'Sole Proprietor': 'soleProprietor',
  'Partnership': 'partnership',
  'Corporation': 'corporation',
  'Subchapter S Corporation': 'subchapterS',
  'Sub-Chapter S Corp': 'subchapterS',
  'LLC': 'llc',
  'Limited Liability Corporation': 'llc',
  'Joint Venture': 'jointVenture',
  'Trust': 'trust',
  'Unincorporated Association': 'unincorporatedAssoc',
  'Not For Profit Org': 'other',
};

const ENTITY_TYPES = [
  { key: 'soleProprietor', label: 'Sole Proprietor' },
  { key: 'partnership', label: 'Partnership' },
  { key: 'corporation', label: 'Corporation' },
  { key: 'subchapterS', label: 'Sub-Chapter S Corp' },
  { key: 'llc', label: 'LLC' },
  { key: 'jointVenture', label: 'Joint Venture' },
  { key: 'trust', label: 'Trust' },
  { key: 'unincorporatedAssoc', label: 'Unincorporated Assoc' },
  { key: 'other', label: 'Other' },
];

const QUESTION_CODES_P3 = [
  { code: 'ABE', label: 'Does the applicant own, lease, or operate any aircraft or watercraft?' },
  { code: 'ACD', label: 'Are there any past, present, or discontinued operations involving hazardous materials?' },
  { code: 'AAD', label: 'Is any work performed underground or above 15 feet?' },
  { code: 'KAR', label: 'Is any work performed on vessels, docks, or bridges?' },
  { code: 'KAS', label: 'Is the applicant engaged in any other type of business?' },
  { code: 'KAT', label: 'Are subcontractors used?' },
  { code: 'KAU', label: 'Is any work sublet without certificates of insurance?' },
  { code: 'ABC', label: 'Is there a written safety program in operation?' },
  { code: 'ABI', label: 'Is any group transportation provided?' },
  { code: 'AAE', label: 'Are any employees under 16 or over 60 years of age?' },
  { code: 'KAV', label: 'Are there any seasonal employees?' },
  { code: 'AAF', label: 'Is there any volunteer or donated labour?' },
  { code: 'ABJ', label: 'Are there employees with physical handicaps?' },
  { code: 'ABH', label: 'Do employees travel out of state?' },
  { code: 'AAB', label: 'Are athletic teams sponsored?' },
  { code: 'ACB', label: 'Are physicals required after offers of employment are made?' },
  { code: 'ABA', label: 'Is there other insurance with this insurer?' },
  { code: 'AAI', label: 'Has prior coverage been declined, cancelled, or non-renewed in the last 3 years?' },
  { code: 'ABF', label: 'Are employee health plans provided?' },
];

const QUESTION_CODES_P4 = [
  { code: 'KAW', label: 'Do any employees perform work for other businesses?' },
  { code: 'AAG', label: 'Does the applicant lease employees to or from other employers?' },
  { code: 'ABG', label: 'Do employees predominantly work at home?', hasCount: true },
  { code: 'KAX', label: 'Are there any tax liens or bankruptcy within the last 5 years?' },
  { code: 'KAY', label: 'Is there any undisputed unpaid workers compensation premium due?' },
];

function ACORD130({ insured, policy, buildings, additionalInterests, getToken, onDirtyChange, onBack }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({});

  const p = policy || {};
  const bldgs = buildings || [];
  const ins = insured || {};

  useEffect(() => {
    console.log('[ACORD 130] Policy prop:', {
      id: p.id,
      Premium: p.Premium,
      ELEachAccident: p.ELEachAccident,
      ELDiseasePolicy: p.ELDiseasePolicy,
      ELDiseaseEachEmployee: p.ELDiseaseEachEmployee,
      WCTotalEstimatedPremium: p.WCTotalEstimatedPremium,
      WCTotalMinimumPremium: p.WCTotalMinimumPremium,
      WCTotalDepositPremium: p.WCTotalDepositPremium,
    });
    console.log('[ACORD 130] Insured prop:', {
      id: insured?.id,
      EntityType: insured?.EntityType,
      MailingState: insured?.MailingState,
    });
    setForm({
      date: new Date().toISOString().split('T')[0],
      carrier: p.Carrier || '', naicCode: p.NAICCode || '',
      policyNumber: p.PolicyNumber || '',
      effectiveDate: p.EffectiveDate || '', expirationDate: p.ExpirationDate || '',
      ratingEffDate: '', anniversaryDate: '',
      retrospectiveRatingPlan: '',
      // Entity type — derive from insured.EntityType
      entityType: ENTITY_TYPE_MAP[insured?.EntityType] || '',
      entityOtherDesc: '',
      // Part One states (10 slots) — default to insured mailing state or TX
      partOneStates: [(insured?.MailingState || 'TX'), '','','','','','','','',''],
      // EL Limits — read from policy
      elEachAccident: p.ELEachAccident || '',
      elDiseasePolicy: p.ELDiseasePolicy || '',
      elDiseaseEachEmployee: p.ELDiseaseEachEmployee || '',
      // Part Three states (10 slots)
      partThreeStates: ['','','','','','','','','',''],
      // Deductible
      deductibleType: '', // medical, indemnity, other
      deductibleTypeOther: '',
      deductibleAmount: '',
      // Coverage options
      covUSLH: false, covVoluntary: false, covForeign: false,
      covManagedCare: false, covOtherA: false, covOtherB: false,
      covOtherDescA: '', covOtherDescB: '',
      dividendPlan: '', additionalCompanyInfo: '', additionalEndorsements: '',
      // Premium totals — default to policy values
      totalEstimatedPremium: p.WCTotalEstimatedPremium || p.Premium || '',
      totalMinimumPremium: p.WCTotalMinimumPremium || p.MinimumPremium || '',
      totalDepositPremium: p.WCTotalDepositPremium || '',
      // Contacts — default to primary contact from insured
      inspectionName: ins.PrimaryContact || '', inspectionPhone: ins.BusinessPhone || '', inspectionCell: '', inspectionEmail: ins.EmailAddress || '',
      accountingName: ins.PrimaryContact || '', accountingPhone: ins.BusinessPhone || '', accountingCell: '', accountingEmail: ins.EmailAddress || '',
      claimsName: ins.PrimaryContact || '', claimsPhone: ins.BusinessPhone || '', claimsCell: '', claimsEmail: ins.EmailAddress || '',
      // 3 locations — first location defaults to insured mailing address if no buildings
      locations: [0,1,2].map(i => {
        const b = bldgs[i] || {};
        if (i === 0 && !b.id) {
          // Fallback: use insured mailing address for location 1
          return {
            locNum: '1', highestFloor: '',
            address: ins.MailingAddress || '', addressLine2: '',
            city: ins.MailingCity || '', county: '',
            state: ins.MailingState || '', zip: ins.MailingZip || '',
          };
        }
        return {
          locNum: b.id ? String(i + 1) : '',
          highestFloor: '',
          address: b.Address || '', addressLine2: '',
          city: b.City || '', county: b.County || '',
          state: b.State || '', zip: b.Zip || '',
        };
      }),
      // Individual/Officer schedule (4 rows)
      individuals: [0,1,2,3].map(() => ({
        state: '', location: '', name: '', dob: '', title: '',
        ownership: '', duties: '', includedExcluded: '', classCode: '', remuneration: '',
      })),
      // Classification schedule (14 rows A-N)
      classifications: Array.from({ length: 14 }, () => ({
        location: '', classCode: '', description: '', duties: '',
        ftEmployees: '', ptEmployees: '', sic: '', naics: '',
        remuneration: '', rate: '', manualPremium: '',
      })),
      // State premium calculations (18 rows)
      stateName: ins.MailingState || 'Texas',
      stateTotalFactored: '',
      stateModifiers: {
        increasedLimitsMod: '', increasedLimitsPrem: '',
        deductibleMod: '', deductiblePrem: '',
        experienceMod: p.ExperienceMod || '', experiencePrem: '',
        terrorismPrem: '', catastrophePrem: '',
        assignedRiskSurchargeMod: '', assignedRiskSurchargePrem: '',
        assignedRiskAddlMod: '', assignedRiskAddlPrem: '',
        otherDescA: '', otherModA: '', otherPremA: '',
        scheduleRatingMod: '', scheduleRatingPrem: '',
        contractingClassMod: '', contractingClassPrem: '',
        standardPremMod: '', standardPremPrem: '',
        premDiscountMod: '', premDiscountPrem: '',
        expenseConstantPrem: '', taxesFeesPrem: '',
        otherDescB: '', otherModB: '', otherPremB: '',
      },
      stateEstimatedPrem: '', stateMinPrem: '', stateDepositPrem: '',
      remarks: '',
      // Prior coverage (5 rows) — populated from SharePoint via useEffect
      priorCoverage: [0,1,2,3,4].map(() => ({
        carrier: '', year: '', premium: '', mod: '',
        claims: '', paid: '', reserved: '', policyNum: '',
      })),
      operationsDesc: ins.OperationsDescription || '',
      // Questions (19 on page 3 + 5 on page 4)
      questions: {},
      questionExplanations: {},
      questionAtHomeCount: '',
      // Policy status
      policyStatus: '', // quote, bound, assignedRisk, issue
      // Payment
      paymentType: '', // producerBill, directBill
      paymentFreq: '', // annual, semiAnnual, quarterly, other
      paymentSchedule: '', downPaymentPct: '',
      // Audit
      auditFreq: '', // atExpiration, semiAnnual, quarterly, monthly, other
      auditFreqCode: '',
      // Participating
      participating: '', // yes, no
      // Loss run
      lossRunAttached: false,
      infoPracticesNotice: false,
    });
    if (onDirtyChange) onDirtyChange(false);
  }, [policy?.id]);

  // Load WC-General questions, Officers, WCRating, CarrierHistory from SharePoint
  useEffect(() => {
    if (!getToken || !insured?.id) return;
    (async () => {
      try {
        const token = await getToken();
        const insId = String(insured.id);

        // Parallel fetch all supporting data
        const [allGQ, allOfficers, allWCR, allCH] = await Promise.all([
          getItems(token, FIG_LISTS.GENERAL_QUESTIONS),
          getItems(token, FIG_LISTS.OFFICERS),
          getItems(token, FIG_LISTS.WC_RATING),
          getItems(token, FIG_LISTS.CARRIER_HISTORY),
        ]);

        // --- Questions ---
        const myGQ = allGQ.filter(q => String(q.InsuredId) === insId && q.FormType === 'WC-General');
        const questions = {};
        const questionExplanations = {};
        for (const q of myGQ) {
          const qNum = Number(q.QuestionNumber);
          let code = null;
          if (qNum >= 1 && qNum <= QUESTION_CODES_P3.length) {
            code = QUESTION_CODES_P3[qNum - 1].code;
          } else if (qNum > QUESTION_CODES_P3.length && qNum <= QUESTION_CODES_P3.length + QUESTION_CODES_P4.length) {
            code = QUESTION_CODES_P4[qNum - QUESTION_CODES_P3.length - 1].code;
          }
          if (!code) continue;
          questions[code] = (q.Answer || '').trim().charAt(0).toUpperCase();
          if (q.Explanation) questionExplanations[code] = q.Explanation;
        }
        console.log(`[ACORD 130] Mapped ${Object.keys(questions).length} question codes`);

        // --- WC Rating (load first — needed for officer cross-reference) ---
        const myWCR = allWCR.filter(w => String(w.InsuredId) === insId);
        // Find executive/officer class code (560x) for cross-referencing officers
        const execWCR = myWCR.find(w => (w.ClassCode || '').startsWith('560'));

        // --- Officers → individuals schedule ---
        const myOfficers = allOfficers.filter(o => String(o.InsuredId) === insId);
        const individuals = [0,1,2,3].map(i => {
          const o = myOfficers[i];
          if (!o) return { state: '', location: '', name: '', dob: '', title: '', ownership: '', duties: '', includedExcluded: '', classCode: '', remuneration: '' };
          // Map Included/Excluded to INC/EXC
          const wcRaw = (o.WCIncluded || o.IncludedExcluded || '').toLowerCase();
          const incExc = wcRaw.startsWith('inc') ? 'INC' : wcRaw.startsWith('exc') ? 'EXC' : '';
          // Cross-reference class code and remuneration from executive WCRating
          const officerClassCode = o.WCClassCode || (execWCR ? execWCR.ClassCode : '');
          const officerRemuneration = o.Remuneration || (execWCR && execWCR.EstimatedPayroll != null ? String(execWCR.EstimatedPayroll) : '');
          return {
            state: o.OfficerState || '',
            location: o.LocationNumber || '1',
            name: o.OfficerName || '',
            dob: o.DateOfBirth || '',
            title: o.OfficerTitle || '',
            ownership: o.OwnershipPct || '',
            duties: o.Duties || '',
            includedExcluded: incExc,
            classCode: officerClassCode,
            remuneration: officerRemuneration,
          };
        });
        console.log(`[ACORD 130] Loaded ${myOfficers.length} officers`);

        // --- WC Rating → classification schedule + premium calculations ---
        const classifications = Array.from({ length: 14 }, (_, i) => {
          const w = myWCR[i];
          if (!w) return { location: '', classCode: '', description: '', duties: '', ftEmployees: '', ptEmployees: '', sic: '', naics: '', remuneration: '', rate: '', manualPremium: '' };
          const payroll = w.EstimatedPayroll != null ? Number(w.EstimatedPayroll) : 0;
          const rate = w.Rate != null ? Number(w.Rate) : 0;
          const manualPrem = w.ManualPremium != null ? Number(w.ManualPremium) : Math.round(payroll * rate / 100 * 100) / 100;
          return {
            location: w.LocationNumber || '',
            classCode: w.ClassCode || '',
            description: w.ClassDescription || '',
            duties: '',
            ftEmployees: w.FullTimeEmployees != null ? String(w.FullTimeEmployees) : '',
            ptEmployees: w.PartTimeEmployees != null ? String(w.PartTimeEmployees) : '',
            sic: '',
            naics: '',
            remuneration: payroll ? String(payroll) : '',
            rate: rate ? String(rate) : '',
            manualPremium: manualPrem ? String(Math.round(manualPrem)) : '',
          };
        });
        // Calculate total manual premium across all class codes
        const totalManualPremium = myWCR.reduce((sum, w) => {
          const payroll = Number(w.EstimatedPayroll) || 0;
          const rate = Number(w.Rate) || 0;
          const manual = w.ManualPremium != null ? Number(w.ManualPremium) : payroll * rate / 100;
          return sum + manual;
        }, 0);
        const expMod = Number(policy?.ExperienceMod) || 1;
        const experienceFactoredPrem = Math.round(totalManualPremium * expMod);
        const estimatedAnnualPrem = experienceFactoredPrem; // Before other modifiers
        console.log(`[ACORD 130] Loaded ${myWCR.length} WC rating records, totalManualPrem=${totalManualPremium.toFixed(2)}, expMod=${expMod}, estimatedPrem=${estimatedAnnualPrem}`);

        // --- Carrier History → prior coverage ---
        const myCH = allCH.filter(c => String(c.InsuredId) === insId && (c.LOB || '').toLowerCase().includes('workers'));
        const priorCoverage = [0,1,2,3,4].map(i => {
          const c = myCH[i];
          if (!c) return { carrier: '', year: '', premium: '', mod: '', claims: '', paid: '', reserved: '', policyNum: '' };
          return {
            carrier: c.Carrier || '',
            year: c.PolicyYear || '',
            premium: c.Premium || '',
            mod: '',
            claims: '',
            paid: '',
            reserved: '',
            policyNum: c.PolicyNumber || '',
          };
        });
        console.log(`[ACORD 130] Loaded ${myCH.length} carrier history records`);

        setForm(prev => ({
          ...prev,
          questions,
          questionExplanations,
          individuals,
          classifications,
          priorCoverage,
          operationsDesc: insured?.OperationsDescription || prev.operationsDesc || '',
          // Premium calculations
          stateTotalFactored: totalManualPremium ? String(Math.round(totalManualPremium)) : '',
          stateModifiers: {
            ...prev.stateModifiers,
            experienceMod: policy?.ExperienceMod || prev.stateModifiers?.experienceMod || '',
            experiencePrem: experienceFactoredPrem ? String(experienceFactoredPrem) : '',
          },
          stateEstimatedPrem: estimatedAnnualPrem ? String(estimatedAnnualPrem) : '',
          stateMinPrem: policy?.MinimumPremium || prev.stateMinPrem || '',
          totalEstimatedPremium: estimatedAnnualPrem ? String(estimatedAnnualPrem) : prev.totalEstimatedPremium || '',
          totalMinimumPremium: policy?.MinimumPremium || prev.totalMinimumPremium || '',
          stateName: insured?.MailingState || 'Texas',
        }));
      } catch (err) {
        console.error('[ACORD 130] Failed to load supporting data:', err.message);
      }
    })();
  }, [insured?.id]);

  function set(key, val) {
    setForm(prev => ({ ...prev, [key]: val }));
    if (onDirtyChange) onDirtyChange(true);
  }

  function setNested(parent, key, val) {
    setForm(prev => ({ ...prev, [parent]: { ...prev[parent], [key]: val } }));
    if (onDirtyChange) onDirtyChange(true);
  }

  function setArrayItem(arrKey, idx, key, val) {
    setForm(prev => {
      const arr = [...(prev[arrKey] || [])];
      arr[idx] = { ...arr[idx], [key]: val };
      return { ...prev, [arrKey]: arr };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  function setStateArr(arrKey, idx, val) {
    setForm(prev => {
      const arr = [...(prev[arrKey] || [])];
      arr[idx] = val;
      return { ...prev, [arrKey]: arr };
    });
    if (onDirtyChange) onDirtyChange(true);
  }

  async function handleSave() {
    if (!p.id || !getToken) return;
    setSaving(true); setSaveMsg('');
    try {
      const token = await getToken();
      await updateItem(token, FIG_LISTS.POLICIES, p.id, {
        Carrier: form.carrier, NAICCode: form.naicCode,
        PolicyNumber: form.policyNumber, EffectiveDate: form.effectiveDate,
        ELEachAccident: form.elEachAccident ? Number(form.elEachAccident) : null,
        ELDiseasePolicy: form.elDiseasePolicy ? Number(form.elDiseasePolicy) : null,
        ELDiseaseEachEmployee: form.elDiseaseEachEmployee ? Number(form.elDiseaseEachEmployee) : null,
        WCTotalEstimatedPremium: form.totalEstimatedPremium ? Number(form.totalEstimatedPremium) : null,
        WCTotalMinimumPremium: form.totalMinimumPremium ? Number(form.totalMinimumPremium) : null,
        WCTotalDepositPremium: form.totalDepositPremium ? Number(form.totalDepositPremium) : null,
        DeductibleType: form.deductibleType || null,
        WCDeductibleAmount: form.deductibleAmount ? Number(form.deductibleAmount) : null,
      });
      setSaveMsg('Saved');
      if (onDirtyChange) onDirtyChange(false);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) { setSaveMsg('Error: ' + err.message); }
    finally { setSaving(false); }
  }

  // Page nav
  const totalPages = 4;
  const navBtnStyle = (disabled) => ({
    width: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.1rem', fontWeight: 700, background: disabled ? '#ccc' : '#7eb8da',
    color: '#fff', border: 'none', borderRadius: '4px', cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1, fontFamily: 'inherit',
  });
  const pageNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center', margin: '0.75rem 0' }}>
      <button style={navBtnStyle(currentPage === 1)} disabled={currentPage === 1}
        onClick={() => setCurrentPage(pg => Math.max(1, pg - 1))}>&lsaquo;</button>
      <span style={{ fontSize: '0.8rem', color: '#666' }}>Page {currentPage} of {totalPages}</span>
      <button style={navBtnStyle(currentPage === totalPages)} disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(pg => Math.min(totalPages, pg + 1))}>&rsaquo;</button>
    </div>
  );

  const tblTh = { padding: '2px 3px', textAlign: 'left', fontSize: '0.55rem', fontWeight: 700, color: '#444' };
  const tblTd = { padding: '2px' };

  return (
    <div className="acord-form">
      <div className="acord-header">
        <div className="acord-header-left">
          <span className="acord-logo">ACORD</span>
          <span className="acord-form-title">ACORD 130 — WORKERS COMPENSATION APPLICATION</span>
        </div>
        <div className="acord-header-right">
          <span style={{ fontSize: '0.8rem', color: '#ccc' }}>Page {currentPage} of {totalPages}</span>
        </div>
      </div>

      {/* ========== PAGE 1 ========== */}
      {currentPage === 1 && <>
        {/* Header / Producer */}
        <div className="acord-section">
          <div className="acord-section-bar">POLICY INFORMATION</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">DATE (MM/DD/YYYY)</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell acord-cell--2"><span className="acord-label">PRODUCER</span>
                <input className="acord-input" value="Fragos Insurance Group LLC" readOnly /></div>
              <div className="acord-cell"><span className="acord-label">CARRIER / COMPANY</span>
                <input className="acord-input" value={form.carrier} readOnly /></div>
              <div className="acord-cell acord-cell--xs"><span className="acord-label">NAIC CODE</span>
                <input className="acord-input" value={form.naicCode} readOnly /></div>
            </div>
          </div>
        </div>

        {/* Named Insured */}
        <div className="acord-section">
          <div className="acord-section-bar">NAMED INSURED</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell acord-cell--2"><span className="acord-label">NAME</span>
                <input className="acord-input" value={ins.CompanyName || ''} readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">SIC</span>
                <input className="acord-input" value={ins.SICCode || ''} readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">NAICS</span>
                <input className="acord-input" value={ins.NAICSCode || ''} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">MAILING ADDRESS</span>
                <input className="acord-input" value={ins.Address || ''} readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">CITY</span>
                <input className="acord-input" value={ins.City || ''} readOnly /></div>
              <div className="acord-cell acord-cell--xs"><span className="acord-label">ST</span>
                <input className="acord-input" value={ins.State || ''} readOnly /></div>
              <div className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
                <input className="acord-input" value={ins.Zip || ''} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell acord-cell--sm"><span className="acord-label">FEIN</span>
                <input className="acord-input" value={ins.FEIN || ins.TaxIdentifier || ''} readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">NCCI RISK ID</span>
                <input className="acord-input" value={ins.NCCIRiskId || ''} readOnly /></div>
              <div className="acord-cell acord-cell--sm"><span className="acord-label">YRS IN BUSINESS</span>
                <input className="acord-input" value={ins.YearsInBusiness || ''} readOnly /></div>
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">ENTITY TYPE</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '2px' }}>
                  {ENTITY_TYPES.map(et => (
                    <label key={et.key} className="acord-checkbox">
                      <input type="radio" name="entityType" value={et.key}
                        checked={form.entityType === et.key}
                        onChange={e => set('entityType', e.target.value)} />
                      <span>{et.label}</span>
                    </label>
                  ))}
                </div>
              </label>
            </div>
            {form.entityType === 'other' && (
              <div className="acord-row">
                <label className="acord-cell"><span className="acord-label">OTHER ENTITY DESCRIPTION</span>
                  <input className="acord-input" value={form.entityOtherDesc} onChange={e => set('entityOtherDesc', e.target.value)} /></label>
              </div>
            )}
          </div>
        </div>

        {/* Locations (3) */}
        {[0,1,2].map(i => {
          const loc = (form.locations || [])[i] || {};
          const suffix = ['A','B','C'][i];
          return (
            <div className="acord-section" key={i}>
              <div className="acord-section-bar">LOCATION {suffix}</div>
              <div className="acord-section-body">
                <div className="acord-row">
                  <label className="acord-cell acord-cell--xs"><span className="acord-label">LOC #</span>
                    <input className="acord-input" value={loc.locNum} onChange={e => setArrayItem('locations', i, 'locNum', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--xs"><span className="acord-label">HIGHEST FLOOR</span>
                    <input className="acord-input" value={loc.highestFloor} onChange={e => setArrayItem('locations', i, 'highestFloor', e.target.value)} /></label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell acord-cell--2"><span className="acord-label">ADDRESS</span>
                    <input className="acord-input" value={loc.address} onChange={e => setArrayItem('locations', i, 'address', e.target.value)} /></label>
                </div>
                <div className="acord-row">
                  <label className="acord-cell"><span className="acord-label">CITY</span>
                    <input className="acord-input" value={loc.city} onChange={e => setArrayItem('locations', i, 'city', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">COUNTY</span>
                    <input className="acord-input" value={loc.county} onChange={e => setArrayItem('locations', i, 'county', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--xs"><span className="acord-label">ST</span>
                    <input className="acord-input" value={loc.state} onChange={e => setArrayItem('locations', i, 'state', e.target.value)} maxLength={2} /></label>
                  <label className="acord-cell acord-cell--xs"><span className="acord-label">ZIP</span>
                    <input className="acord-input" value={loc.zip} onChange={e => setArrayItem('locations', i, 'zip', e.target.value)} /></label>
                </div>
              </div>
            </div>
          );
        })}

        {/* Policy Dates */}
        <div className="acord-section">
          <div className="acord-section-bar">POLICY DATES</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">EFFECTIVE DATE</span>
                <input className="acord-input" type="date" value={form.effectiveDate} onChange={e => set('effectiveDate', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">EXPIRATION DATE</span>
                <input className="acord-input" type="date" value={form.expirationDate} onChange={e => set('expirationDate', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">RATING EFF DATE</span>
                <input className="acord-input" type="date" value={form.ratingEffDate} onChange={e => set('ratingEffDate', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">ANNIVERSARY DATE</span>
                <input className="acord-input" type="date" value={form.anniversaryDate} onChange={e => set('anniversaryDate', e.target.value)} /></label>
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">RETROSPECTIVE RATING PLAN</span>
                <input className="acord-input" value={form.retrospectiveRatingPlan} onChange={e => set('retrospectiveRatingPlan', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Part One - WC States */}
        <div className="acord-section">
          <div className="acord-section-bar">PART ONE — WORKERS COMPENSATION (states)</div>
          <div className="acord-section-body">
            <div className="acord-row" style={{ flexWrap: 'wrap' }}>
              {(form.partOneStates || []).map((st, i) => (
                <label className="acord-cell acord-cell--xs" key={i}><span className="acord-label">{i + 1}</span>
                  <input className="acord-input" value={st} maxLength={2} style={{ textTransform: 'uppercase' }}
                    onChange={e => setStateArr('partOneStates', i, e.target.value.toUpperCase())} /></label>
              ))}
            </div>
          </div>
        </div>

        {/* EL Limits */}
        <div className="acord-section">
          <div className="acord-section-bar">PART TWO — EMPLOYERS LIABILITY LIMITS</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">EACH ACCIDENT</span>
                <input className="acord-input" type="number" value={form.elEachAccident} onChange={e => set('elEachAccident', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">DISEASE — POLICY LIMIT</span>
                <input className="acord-input" type="number" value={form.elDiseasePolicy} onChange={e => set('elDiseasePolicy', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">DISEASE — EACH EMPLOYEE</span>
                <input className="acord-input" type="number" value={form.elDiseaseEachEmployee} onChange={e => set('elDiseaseEachEmployee', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Part Three - Other States */}
        <div className="acord-section">
          <div className="acord-section-bar">PART THREE — OTHER STATES INSURANCE (states)</div>
          <div className="acord-section-body">
            <div className="acord-row" style={{ flexWrap: 'wrap' }}>
              {(form.partThreeStates || []).map((st, i) => (
                <label className="acord-cell acord-cell--xs" key={i}><span className="acord-label">{i + 1}</span>
                  <input className="acord-input" value={st} maxLength={2} style={{ textTransform: 'uppercase' }}
                    onChange={e => setStateArr('partThreeStates', i, e.target.value.toUpperCase())} /></label>
              ))}
            </div>
          </div>
        </div>

        {/* Deductible */}
        <div className="acord-section">
          <div className="acord-section-bar">DEDUCTIBLE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell" style={{ flexDirection: 'row', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {[['medical','Medical'],['indemnity','Indemnity'],['other','Other']].map(([val,lbl]) => (
                  <label className="acord-checkbox" key={val}>
                    <input type="radio" name="deductibleType" value={val}
                      checked={form.deductibleType === val} onChange={e => set('deductibleType', e.target.value)} />
                    <span>{lbl}</span>
                  </label>
                ))}
              </div>
              {form.deductibleType === 'other' && (
                <label className="acord-cell"><span className="acord-label">OTHER DESC</span>
                  <input className="acord-input" value={form.deductibleTypeOther} onChange={e => set('deductibleTypeOther', e.target.value)} /></label>
              )}
              <label className="acord-cell acord-cell--sm"><span className="acord-label">AMOUNT</span>
                <input className="acord-input" type="number" value={form.deductibleAmount} onChange={e => set('deductibleAmount', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Coverage Options */}
        <div className="acord-section">
          <div className="acord-section-bar">COVERAGE OPTIONS</div>
          <div className="acord-section-body">
            <div className="acord-row" style={{ flexWrap: 'wrap' }}>
              {[['covUSLH','USL&H'],['covVoluntary','Voluntary Comp'],['covForeign','Foreign Coverage'],
                ['covManagedCare','Managed Care Option']].map(([key,lbl]) => (
                <label className="acord-checkbox" key={key} style={{ marginRight: '1rem' }}>
                  <input type="checkbox" checked={form[key] || false} onChange={e => set(key, e.target.checked)} />
                  <span>{lbl}</span>
                </label>
              ))}
            </div>
            <div className="acord-row">
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.covOtherA || false} onChange={e => set('covOtherA', e.target.checked)} />
                <span>Other</span>
              </label>
              {form.covOtherA && (
                <label className="acord-cell"><span className="acord-label">DESCRIBE</span>
                  <input className="acord-input" value={form.covOtherDescA} onChange={e => set('covOtherDescA', e.target.value)} /></label>
              )}
              <label className="acord-checkbox">
                <input type="checkbox" checked={form.covOtherB || false} onChange={e => set('covOtherB', e.target.checked)} />
                <span>Other</span>
              </label>
              {form.covOtherB && (
                <label className="acord-cell"><span className="acord-label">DESCRIBE</span>
                  <input className="acord-input" value={form.covOtherDescB} onChange={e => set('covOtherDescB', e.target.value)} /></label>
              )}
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">DIVIDEND / SAFETY PLAN</span>
                <input className="acord-input" value={form.dividendPlan} onChange={e => set('dividendPlan', e.target.value)} /></label>
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">ADDITIONAL COMPANY INFORMATION</span>
                <input className="acord-input" value={form.additionalCompanyInfo} onChange={e => set('additionalCompanyInfo', e.target.value)} /></label>
            </div>
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">ADDITIONAL COVERAGE / ENDORSEMENT DESCRIPTION</span>
                <input className="acord-input" value={form.additionalEndorsements} onChange={e => set('additionalEndorsements', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Premium Totals */}
        <div className="acord-section">
          <div className="acord-section-bar">PREMIUM</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">ESTIMATED ANNUAL PREMIUM (ALL STATES)</span>
                <input className="acord-input" type="number" value={form.totalEstimatedPremium} onChange={e => set('totalEstimatedPremium', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">MINIMUM PREMIUM (ALL STATES)</span>
                <input className="acord-input" type="number" value={form.totalMinimumPremium} onChange={e => set('totalMinimumPremium', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">DEPOSIT PREMIUM (ALL STATES)</span>
                <input className="acord-input" type="number" value={form.totalDepositPremium} onChange={e => set('totalDepositPremium', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Contacts */}
        <div className="acord-section">
          <div className="acord-section-bar">CONTACTS</div>
          <div className="acord-section-body">
            {[['Inspection','inspection'],['Accounting','accounting'],['Claims','claims']].map(([lbl,prefix]) => (
              <div key={prefix}>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#555', padding: '4px 0 2px 4px', textTransform: 'uppercase' }}>{lbl} Contact</div>
                <div className="acord-row">
                  <label className="acord-cell"><span className="acord-label">NAME</span>
                    <input className="acord-input" value={form[prefix + 'Name'] || ''} onChange={e => set(prefix + 'Name', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">PHONE</span>
                    <input className="acord-input" value={form[prefix + 'Phone'] || ''} onChange={e => set(prefix + 'Phone', e.target.value)} /></label>
                  <label className="acord-cell acord-cell--sm"><span className="acord-label">CELL</span>
                    <input className="acord-input" value={form[prefix + 'Cell'] || ''} onChange={e => set(prefix + 'Cell', e.target.value)} /></label>
                  <label className="acord-cell"><span className="acord-label">EMAIL</span>
                    <input className="acord-input" value={form[prefix + 'Email'] || ''} onChange={e => set(prefix + 'Email', e.target.value)} /></label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Individual / Officer Schedule */}
        <div className="acord-section">
          <div className="acord-section-bar">INDIVIDUAL / OFFICER SCHEDULE</div>
          <div className="acord-section-body" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead><tr style={{ background: '#eef2f7' }}>
                {['ST','LOC','NAME','DOB','TITLE','OWN%','DUTIES','INC/EXC','CLASS','REMUN'].map(h => (
                  <th key={h} style={tblTh}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(form.individuals || []).map((ind, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={tblTd}><input className="acord-input" value={ind.state} onChange={e => setArrayItem('individuals', i, 'state', e.target.value)} style={{ width: '2rem' }} maxLength={2} /></td>
                    <td style={tblTd}><input className="acord-input" value={ind.location} onChange={e => setArrayItem('individuals', i, 'location', e.target.value)} style={{ width: '2rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={ind.name} onChange={e => setArrayItem('individuals', i, 'name', e.target.value)} style={{ width: '7rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="date" value={ind.dob} onChange={e => setArrayItem('individuals', i, 'dob', e.target.value)} style={{ width: '6rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={ind.title} onChange={e => setArrayItem('individuals', i, 'title', e.target.value)} style={{ width: '4rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={ind.ownership} onChange={e => setArrayItem('individuals', i, 'ownership', e.target.value)} style={{ width: '3rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={ind.duties} onChange={e => setArrayItem('individuals', i, 'duties', e.target.value)} style={{ width: '5rem' }} /></td>
                    <td style={tblTd}><select className="acord-input" value={ind.includedExcluded} onChange={e => setArrayItem('individuals', i, 'includedExcluded', e.target.value)} style={{ width: '4rem', fontSize: '0.7rem' }}>
                      <option value="">--</option><option value="I">Included</option><option value="E">Excluded</option>
                    </select></td>
                    <td style={tblTd}><input className="acord-input" value={ind.classCode} onChange={e => setArrayItem('individuals', i, 'classCode', e.target.value)} style={{ width: '3.5rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="number" value={ind.remuneration} onChange={e => setArrayItem('individuals', i, 'remuneration', e.target.value)} style={{ width: '5rem' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {pageNav}
      </>}

      {/* ========== PAGE 2 ========== */}
      {currentPage === 2 && <>
        {/* Classification Schedule */}
        <div className="acord-section">
          <div className="acord-section-bar">CLASSIFICATION SCHEDULE (A-N)</div>
          <div className="acord-section-body" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead><tr style={{ background: '#eef2f7' }}>
                {['#','LOC','CODE','DESCRIPTION','DUTIES','FT','PT','SIC','NAICS','REMUN','RATE','MAN PREM'].map(h => (
                  <th key={h} style={tblTh}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(form.classifications || []).map((cls, i) => {
                  const letter = String.fromCharCode(65 + i);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ ...tblTd, fontWeight: 700, fontSize: '0.65rem', color: '#666' }}>{letter}</td>
                      <td style={tblTd}><input className="acord-input" value={cls.location} onChange={e => setArrayItem('classifications', i, 'location', e.target.value)} style={{ width: '2rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.classCode} onChange={e => setArrayItem('classifications', i, 'classCode', e.target.value)} style={{ width: '3.5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.description} onChange={e => setArrayItem('classifications', i, 'description', e.target.value)} style={{ width: '6rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.duties} onChange={e => setArrayItem('classifications', i, 'duties', e.target.value)} style={{ width: '5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" type="number" value={cls.ftEmployees} onChange={e => setArrayItem('classifications', i, 'ftEmployees', e.target.value)} style={{ width: '2.5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" type="number" value={cls.ptEmployees} onChange={e => setArrayItem('classifications', i, 'ptEmployees', e.target.value)} style={{ width: '2.5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.sic} onChange={e => setArrayItem('classifications', i, 'sic', e.target.value)} style={{ width: '3rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.naics} onChange={e => setArrayItem('classifications', i, 'naics', e.target.value)} style={{ width: '3.5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" type="number" value={cls.remuneration} onChange={e => setArrayItem('classifications', i, 'remuneration', e.target.value)} style={{ width: '5rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" value={cls.rate} onChange={e => setArrayItem('classifications', i, 'rate', e.target.value)} style={{ width: '3rem' }} /></td>
                      <td style={tblTd}><input className="acord-input" type="number" value={cls.manualPremium} onChange={e => setArrayItem('classifications', i, 'manualPremium', e.target.value)} style={{ width: '5rem' }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* State Premium Calculations */}
        <div className="acord-section">
          <div className="acord-section-bar">STATE PREMIUM CALCULATIONS</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <label className="acord-cell"><span className="acord-label">STATE</span>
                <input className="acord-input" value={form.stateName} onChange={e => set('stateName', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">TOTAL FACTORED PREMIUM</span>
                <input className="acord-input" type="number" value={form.stateTotalFactored} onChange={e => set('stateTotalFactored', e.target.value)} /></label>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              <thead><tr style={{ background: '#eef2f7' }}>
                <th style={tblTh}>MODIFIER / ITEM</th>
                <th style={tblTh}>FACTOR</th>
                <th style={tblTh}>PREMIUM</th>
              </tr></thead>
              <tbody>
                {[
                  ['Increased Limits', 'increasedLimitsMod', 'increasedLimitsPrem'],
                  ['Deductible', 'deductibleMod', 'deductiblePrem'],
                  ['Experience / Merit', 'experienceMod', 'experiencePrem'],
                  ['Terrorism', null, 'terrorismPrem'],
                  ['Catastrophe', null, 'catastrophePrem'],
                  ['Assigned Risk Surcharge', 'assignedRiskSurchargeMod', 'assignedRiskSurchargePrem'],
                  ['Assigned Risk Addl Premium', 'assignedRiskAddlMod', 'assignedRiskAddlPrem'],
                  ['Other (A)', 'otherModA', 'otherPremA', 'otherDescA'],
                  ['Schedule Rating', 'scheduleRatingMod', 'scheduleRatingPrem'],
                  ['Contracting Class Prem Adj', 'contractingClassMod', 'contractingClassPrem'],
                  ['Standard Premium', 'standardPremMod', 'standardPremPrem'],
                  ['Premium Discount', 'premDiscountMod', 'premDiscountPrem'],
                  ['Expense Constant', null, 'expenseConstantPrem'],
                  ['Taxes / Fees / Assessment', null, 'taxesFeesPrem'],
                  ['Other (B)', 'otherModB', 'otherPremB', 'otherDescB'],
                ].map(([label, modKey, premKey, descKey], idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={tblTd}>
                      {descKey ? (
                        <input className="acord-input" placeholder={label} value={(form.stateModifiers || {})[descKey] || ''}
                          onChange={e => setNested('stateModifiers', descKey, e.target.value)} style={{ width: '10rem' }} />
                      ) : (
                        <span style={{ fontSize: '0.7rem', color: '#555', paddingLeft: '4px' }}>{label}</span>
                      )}
                    </td>
                    <td style={tblTd}>
                      {modKey ? <input className="acord-input" value={(form.stateModifiers || {})[modKey] || ''}
                        onChange={e => setNested('stateModifiers', modKey, e.target.value)} style={{ width: '4rem' }} />
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={tblTd}>
                      <input className="acord-input" type="number" value={(form.stateModifiers || {})[premKey] || ''}
                        onChange={e => setNested('stateModifiers', premKey, e.target.value)} style={{ width: '5rem' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="acord-row" style={{ marginTop: '0.5rem' }}>
              <label className="acord-cell"><span className="acord-label">STATE EST ANNUAL PREMIUM</span>
                <input className="acord-input" type="number" value={form.stateEstimatedPrem} onChange={e => set('stateEstimatedPrem', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">STATE MINIMUM PREMIUM</span>
                <input className="acord-input" type="number" value={form.stateMinPrem} onChange={e => set('stateMinPrem', e.target.value)} /></label>
              <label className="acord-cell"><span className="acord-label">STATE DEPOSIT PREMIUM</span>
                <input className="acord-input" type="number" value={form.stateDepositPrem} onChange={e => set('stateDepositPrem', e.target.value)} /></label>
            </div>
          </div>
        </div>

        {/* Remarks */}
        <div className="acord-section">
          <div className="acord-section-bar">REMARKS</div>
          <div className="acord-section-body">
            <textarea className="acord-input" rows={4} value={form.remarks}
              onChange={e => set('remarks', e.target.value)}
              style={{ resize: 'vertical', minHeight: '3rem', padding: '0.4rem' }}
              placeholder="Additional remarks..." />
          </div>
        </div>
        {pageNav}
      </>}

      {/* ========== PAGE 3 ========== */}
      {currentPage === 3 && <>
        {/* Prior Coverage / Loss History */}
        <div className="acord-section">
          <div className="acord-section-bar">PRIOR COVERAGE / LOSS HISTORY</div>
          <div className="acord-section-body" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead><tr style={{ background: '#eef2f7' }}>
                {['CARRIER','YEAR','PREMIUM','MOD','CLAIMS','PAID','RESERVED','POLICY #'].map(h => (
                  <th key={h} style={tblTh}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(form.priorCoverage || []).map((pc, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={tblTd}><input className="acord-input" value={pc.carrier} onChange={e => setArrayItem('priorCoverage', i, 'carrier', e.target.value)} style={{ width: '8rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={pc.year} onChange={e => setArrayItem('priorCoverage', i, 'year', e.target.value)} style={{ width: '3rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="number" value={pc.premium} onChange={e => setArrayItem('priorCoverage', i, 'premium', e.target.value)} style={{ width: '5rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={pc.mod} onChange={e => setArrayItem('priorCoverage', i, 'mod', e.target.value)} style={{ width: '3rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="number" value={pc.claims} onChange={e => setArrayItem('priorCoverage', i, 'claims', e.target.value)} style={{ width: '3rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="number" value={pc.paid} onChange={e => setArrayItem('priorCoverage', i, 'paid', e.target.value)} style={{ width: '5rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" type="number" value={pc.reserved} onChange={e => setArrayItem('priorCoverage', i, 'reserved', e.target.value)} style={{ width: '5rem' }} /></td>
                    <td style={tblTd}><input className="acord-input" value={pc.policyNum} onChange={e => setArrayItem('priorCoverage', i, 'policyNum', e.target.value)} style={{ width: '6rem' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Operations Description */}
        <div className="acord-section">
          <div className="acord-section-bar">DESCRIPTION OF OPERATIONS</div>
          <div className="acord-section-body">
            <textarea className="acord-input" rows={4} value={form.operationsDesc}
              onChange={e => set('operationsDesc', e.target.value)}
              style={{ resize: 'vertical', minHeight: '3rem', padding: '0.4rem' }}
              placeholder="Describe operations..." />
          </div>
        </div>

        {/* 19 Y/N Questions */}
        <div className="acord-section">
          <div className="acord-section-bar">QUESTIONS</div>
          <div className="acord-section-body">
            {QUESTION_CODES_P3.map((q, i) => (
              <div key={q.code} style={{ borderBottom: '1px solid #eee', padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '1.5rem' }}>{i + 1}.</span>
                  <span style={{ fontSize: '0.75rem', flex: 1 }}>{q.label}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', minWidth: '4rem' }}>
                    <label className="acord-checkbox"><input type="radio" name={`q_${q.code}`} value="Y"
                      checked={(form.questions || {})[q.code] === 'Y'}
                      onChange={() => { set('questions', { ...form.questions, [q.code]: 'Y' }); }} /><span>Y</span></label>
                    <label className="acord-checkbox"><input type="radio" name={`q_${q.code}`} value="N"
                      checked={(form.questions || {})[q.code] === 'N'}
                      onChange={() => { set('questions', { ...form.questions, [q.code]: 'N' }); }} /><span>N</span></label>
                  </div>
                </div>
                {(form.questions || {})[q.code] === 'Y' && (
                  <textarea className="acord-input" rows={2} placeholder="Explain..."
                    value={(form.questionExplanations || {})[q.code] || ''}
                    onChange={e => set('questionExplanations', { ...form.questionExplanations, [q.code]: e.target.value })}
                    style={{ resize: 'vertical', marginTop: '4px', padding: '0.3rem', fontSize: '0.75rem' }} />
                )}
              </div>
            ))}
          </div>
        </div>
        {pageNav}
      </>}

      {/* ========== PAGE 4 ========== */}
      {currentPage === 4 && <>
        {/* 5 more Y/N Questions */}
        <div className="acord-section">
          <div className="acord-section-bar">QUESTIONS (continued)</div>
          <div className="acord-section-body">
            {QUESTION_CODES_P4.map((q, i) => (
              <div key={q.code} style={{ borderBottom: '1px solid #eee', padding: '4px 0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.65rem', color: '#888', minWidth: '1.5rem' }}>{i + 20}.</span>
                  <span style={{ fontSize: '0.75rem', flex: 1 }}>{q.label}</span>
                  <div style={{ display: 'flex', gap: '0.5rem', minWidth: '4rem' }}>
                    <label className="acord-checkbox"><input type="radio" name={`q_${q.code}`} value="Y"
                      checked={(form.questions || {})[q.code] === 'Y'}
                      onChange={() => { set('questions', { ...form.questions, [q.code]: 'Y' }); }} /><span>Y</span></label>
                    <label className="acord-checkbox"><input type="radio" name={`q_${q.code}`} value="N"
                      checked={(form.questions || {})[q.code] === 'N'}
                      onChange={() => { set('questions', { ...form.questions, [q.code]: 'N' }); }} /><span>N</span></label>
                  </div>
                </div>
                {q.hasCount && (form.questions || {})[q.code] === 'Y' && (
                  <div className="acord-row" style={{ marginTop: '4px' }}>
                    <label className="acord-cell acord-cell--xs"><span className="acord-label"># AT HOME</span>
                      <input className="acord-input" value={form.questionAtHomeCount}
                        onChange={e => set('questionAtHomeCount', e.target.value)} /></label>
                  </div>
                )}
                {(form.questions || {})[q.code] === 'Y' && (
                  <textarea className="acord-input" rows={2} placeholder="Explain..."
                    value={(form.questionExplanations || {})[q.code] || ''}
                    onChange={e => set('questionExplanations', { ...form.questionExplanations, [q.code]: e.target.value })}
                    style={{ resize: 'vertical', marginTop: '4px', padding: '0.3rem', fontSize: '0.75rem' }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Signature */}
        <div className="acord-section">
          <div className="acord-section-bar">SIGNATURE</div>
          <div className="acord-section-body">
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">PRODUCER SIGNATURE</span>
                <input className="acord-input" value="Marc Fragos" readOnly style={{ fontStyle: 'italic' }} /></div>
              <div className="acord-cell"><span className="acord-label">TITLE</span>
                <input className="acord-input" value="Owner — Fragos Insurance Group LLC" readOnly /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
            <div className="acord-row">
              <div className="acord-cell"><span className="acord-label">APPLICANT SIGNATURE</span>
                <input className="acord-input" value={ins.CompanyName || ''} readOnly style={{ fontStyle: 'italic' }} /></div>
              <div className="acord-cell"><span className="acord-label">DATE</span>
                <input className="acord-input" value={form.date} readOnly /></div>
            </div>
          </div>
        </div>
        {pageNav}
      </>}

      {/* Actions */}
      <div className="acord-actions">
        <button className="FIG-back-btn" onClick={onBack}>&lsaquo; Back to ACORDs</button>
        <button className="FIG-save-btn" onClick={handleSave} disabled={saving || !p.id}>
          {saving ? 'Saving...' : 'Save Coverage'}
        </button>
        {saveMsg && <span className={'FIG-save-msg' + (saveMsg.startsWith('Error') ? ' FIG-save-msg--error' : '')}>{saveMsg}</span>}
        <button className="FIG-save-btn acord-pdf-btn" disabled={generating} onClick={async () => {
          setGenerating(true);
          try {
            await generateACORD130(form, insured, getToken);
          } catch (err) {
            console.error('[ACORD 130] PDF generation failed:', err);
            alert('Error generating PDF: ' + err.message);
          } finally {
            setGenerating(false);
          }
        }}>
          {generating ? 'Generating...' : 'Generate PDF'}
        </button>
      </div>
    </div>
  );
}

export default ACORD130;
